import {chain} from 'lodash';
import React, {useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {TooltipCoordinator} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {flexCenter} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {isConversationSafetyNetRequiredTagging} from '../../../../../../models/api/conversationSafetyNetModel';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {getRulesRoute} from '../../../../../../settings/rules/ruleHelpers';
import {getHasPermission} from '../../../../../../store/data/access/accessSelectors';
import {ComplexTooltipContent} from '../../../../../common/tooltips/complexTooltipContent';

/*
 * Props.
 */

interface RequiredTaggingTooltipProps {
  className?: string;

  conversations: ReadonlyArray<ConversationBaseModel>;
}

/*
 * Styles.
 */

const StyledTitleDiv = styled.div`
  ${flexCenter}
`;

const StyledTitleIcon = styled(Icon)`
  margin-right: 8px;
`;

const StyledContentDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  display: block;
  width: fit-content;
  align-self: flex-end;
  margin-top: 16px;
`;

/*
 * Compoenent.
 */

export const RequiredTaggingTooltip: React.FC<RequiredTaggingTooltipProps> = (props) => {
  const {className, conversations} = props;
  const {navigateToLink} = useLinkNavigation();

  const hasPermission = useAppSelector(getHasPermission);

  const safetyNet = useMemo(
    () =>
      chain(conversations)
        .map((conversation) => conversation.safetyNets)
        .compact()
        .flatten()
        .value()
        .find((net) => isConversationSafetyNetRequiredTagging(net)),
    [conversations],
  );

  const maybeRenderLinkButton = () => {
    if (!safetyNet) {
      return null;
    }

    if (!hasPermission(safetyNet.namespace, PermissionsEnum.RULE_UPDATE)) {
      return null;
    }

    return (
      <StyledButton
        size={VisualSizesEnum.SMALL}
        onClick={() =>
          navigateToLink(
            `${getRulesRoute(safetyNet.namespace)}/recipes/required-tagging/${safetyNet.ownerId}`,
          )
        }
      >
        <Trans id="required-tagging-tooltip-link">View rule</Trans>
      </StyledButton>
    );
  };

  const renderTooltip = () => (
    <ComplexTooltipContent
      maxWidth={240}
      title={
        <StyledTitleDiv>
          <StyledTitleIcon name="gearFilled" />
          <Trans id="required-tagging-tooltip-title">Tag required by a rule</Trans>
        </StyledTitleDiv>
      }
      body={
        <StyledContentDiv>
          <Trans id="required-tagging-tooltip-body">
            This rule was enabled to ensure incoming messages are categorized.
          </Trans>
          {maybeRenderLinkButton()}
        </StyledContentDiv>
      }
    />
  );

  return <TooltipCoordinator render={renderTooltip} placement="top" className={className} persistent />;
};
